import React, { useState } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/Button'

interface ReasonPromptModalProps {
  isOpen: boolean
  title: string
  description?: string
  confirmLabel: string
  confirmVariant?: 'primary' | 'danger'
  onClose: () => void
  onConfirm: (reason: string) => Promise<void>
  isSubmitting?: boolean
}

/**
 * Every admin mutation in the portal (suspend/reactivate, role-change,
 * force-end-relationship) requires a short, non-empty reason - this modal
 * is the shared confirm-with-reason UI for all of them.
 */
const ReasonPromptModal: React.FC<ReasonPromptModalProps> = ({
  isOpen,
  title,
  description,
  confirmLabel,
  confirmVariant = 'primary',
  onClose,
  onConfirm,
  isSubmitting = false
}) => {
  const [reason, setReason] = useState('')
  const [error, setError] = useState('')

  if (!isOpen) return null

  const handleClose = () => {
    if (!isSubmitting) {
      setReason('')
      setError('')
      onClose()
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!reason.trim()) {
      setError('A reason is required')
      return
    }

    setError('')
    await onConfirm(reason.trim())
    setReason('')
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={handleClose}
        />

        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
            {description && <p className="mt-1 text-sm text-gray-600">{description}</p>}
          </div>

          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label htmlFor="reason" className="block text-sm font-medium text-gray-700 mb-2">
                Reason
              </label>
              <textarea
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Explain why you're taking this action..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
                rows={3}
                disabled={isSubmitting}
                maxLength={500}
                autoFocus
              />
              {error && <span className="text-xs text-red-500 mt-1 block">{error}</span>}
            </div>

            <div className="flex gap-3 justify-end">
              <Button type="button" variant="secondary" onClick={handleClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" variant={confirmVariant} disabled={isSubmitting} isLoading={isSubmitting}>
                {confirmLabel}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default ReasonPromptModal
